import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import api, { API_BASE_URL } from '../services/api';
import toast from '../utils/toast';
import PageTitle from '../components/PageTitle';

const TwoFactorSetup = () => {
  const { settings } = useSettings();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  
  // User data available from location state (passed from login): location.state?.user
  const fromProfile = location.state?.fromProfile || false;

  useEffect(() => {
    // Check if we have required data (sessionToken for login flow, or fromProfile for authenticated flow)
    if (!location.state?.sessionToken && !location.state?.user && !fromProfile) {
      console.error('2FA Setup - Missing required data, redirecting to login');
      toast.error('Session expired. Please login again.');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    
    generateQRCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      
      // SECURITY: Use session token instead of password (or JWT auth if from Profile)
      const sessionToken = location.state?.sessionToken;
      console.log('2FA Setup - Session token:', sessionToken ? 'Present' : 'MISSING');
      console.log('2FA Setup - From Profile:', fromProfile);
      console.log('2FA Setup - Location state:', location.state);
      
      if (!sessionToken && !fromProfile) {
        throw new Error('Session token is missing. Please try logging in again.');
      }
      
      // First-time setup - use session token if available, otherwise API will use JWT auth
      const requestBody = sessionToken ? { sessionToken } : {};
      const response = await api.post('/api/two-factor/setup', requestBody);
      console.log('2FA Setup - Response received:', response.data ? 'Success' : 'Failed');
      
      if (!response.data.qrCode) {
        throw new Error('QR code not generated in response');
      }
      
      setQrCode(response.data.qrCode);
      setManualKey(response.data.manualEntryKey);
      setError(''); // Clear any previous errors
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      console.error('2FA setup error details:', {
        message: errorMessage,
        response: error.response?.data,
        status: error.response?.status,
        fullError: error
      });
      // Show error for longer so user can read it
      toast.error('Error generating QR code: ' + errorMessage, { autoClose: 10000 });
      // Also show error in UI (persistent)
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      // SECURITY: Use session token instead of password (or JWT auth if from Profile)
      const sessionToken = location.state?.sessionToken;
      
      // If coming from Profile (JWT auth), don't pass sessionToken
      const requestBody = {
        token: verificationCode,
        method: 'authenticator'
      };
      if (sessionToken) {
        requestBody.sessionToken = sessionToken;
      }
      
      const response = await api.post('/api/two-factor/verify-setup', requestBody);
      
      toast.success('2FA enabled successfully!');
      
      // If coming from Profile, just refresh user and redirect back
      if (fromProfile) {
        await refreshUser();
        navigate('/profile');
        return;
      }
      
      // Backend now returns JWT token directly after 2FA setup
      if (response.data.token && response.data.user) {
        // Store token and user
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Refresh auth context to pick up the new token
        await refreshUser();
        
        // Redirect to intended destination or home
        // Validate redirect path to prevent open redirect attacks
        const redirectPath = location.state?.from || '/';
        const isValidPath = redirectPath && 
          typeof redirectPath === 'string' &&
          redirectPath.startsWith('/') &&
          !/^https?:\/\//i.test(redirectPath) &&
          !redirectPath.startsWith('//') &&
          !/^(javascript|data):/i.test(redirectPath) &&
          !redirectPath.includes('../');
        
        navigate(isValidPath ? redirectPath : '/');
      } else {
        // Fallback: redirect to login if token not provided
        console.error('No token in response:', response.data);
        toast.error('2FA enabled but login failed. Please login again.');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      toast.error('Invalid verification code. Please try again.');
      setVerificationCode('');
    } finally {
      setVerifying(false);
    }
  };

  const loginStyle = settings?.loginBackgroundImage ? {
    backgroundImage: `url(${API_BASE_URL}${settings.loginBackgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minHeight: '100vh',
    height: '100%'
  } : {
    minHeight: '100vh',
    height: '100%'
  };

  return (
    <div className="page page-center" style={loginStyle}>
      <PageTitle title="Setup Two-Factor Authentication" />
      <div className="container py-4" style={{ maxWidth: '650px' }}>
        <div className="card">
          <div className="card-body">
            <div className="text-center mb-3">
              {settings?.logoLight && (
                <img 
                  src={`${API_BASE_URL}${settings.logoLight}`} 
                  alt={settings.companyName || settings.siteName || 'Logo'} 
                  style={{ maxHeight: '50px', marginBottom: '0.5rem' }}
                />
              )}
              <h2 className="mb-1">Setup Two-Factor Authentication</h2>
              <p className="text-secondary small mb-0">
                Secure your account with an authenticator app
              </p>
            </div>

            {error && (
              <div key={error} className="alert alert-danger login-alert mb-4" role="alert">
                <strong>Error:</strong> {error}
              </div>
            )}

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Generating QR code...</p>
              </div>
            ) : (
              <form onSubmit={handleVerify}>
                <div className="row">
                  {/* Left Column - Step 1: QR Code */}
                  <div className="col-md-6 text-center border-end">
                    <h4 className="mb-3">Step 1: Scan QR Code</h4>
                    <p className="text-secondary small mb-3">
                      Scan with your authenticator app
                    </p>
                    {qrCode && (
                      <div className="mb-3">
                        <img src={qrCode} alt="QR Code" style={{ maxWidth: '180px', border: '1px solid #ddd', borderRadius: '8px', padding: '8px', backgroundColor: 'white' }} />
                      </div>
                    )}
                    <div className="mt-3">
                      <small className="text-muted d-block mb-2">Can't scan? Copy this key:</small>
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control text-center font-monospace"
                          value={manualKey}
                          readOnly
                          style={{ fontSize: '0.75rem' }}
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(manualKey);
                            toast.success('Key copied!');
                          }}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Step 2: Verify */}
                  <div className="col-md-6">
                    <h4 className="mb-3">Step 2: Verify</h4>
                    <p className="text-secondary small mb-3">
                      Enter the 6-digit code from your app
                    </p>
                    <div className="mb-3">
                      <label className="form-label">Verification Code</label>
                      <input
                        type="text"
                        className="form-control text-center font-monospace"
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setVerificationCode(value);
                        }}
                        maxLength="6"
                        required
                        style={{ fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                        autoFocus
                      />
                    </div>
                    <p className="text-muted small">
                      Apps: Google Authenticator, Microsoft Authenticator, Authy, 1Password
                    </p>
                  </div>
                </div>

                <hr className="my-4" />

                <div className="form-footer">
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={verifying || verificationCode.length !== 6}
                  >
                    {verifying ? 'Verifying...' : 'Verify & Enable 2FA'}
                  </button>
                </div>
              </form>
            )}

            {/* Back link - only show if coming from method selection */}
            {location.state?.fromMethodSelect && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-link text-secondary p-0"
                  onClick={() => navigate('/2fa-method-select', {
                    state: {
                      sessionToken: location.state?.sessionToken,
                      user: location.state?.user,
                      allowedMethods: location.state?.allowedMethods || ['authenticator', 'email'],
                      from: location.state?.from
                    }
                  })}
                >
                  Choose a different method
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetup;
